import { createContext, useContext, useEffect, useMemo } from "react";

import { useAuthenticator } from "@aws-amplify/ui-react";
import { Amplify } from "aws-amplify";
import { fetchAuthSession } from "aws-amplify/auth";

import UserApi from "@/api/services/user";
import { identifyUser, registerSuperProperties, resetAnalytics } from "@/utils/analytics";
import { isAgencyUser } from "@/utils/party";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import LogRocket from "logrocket";

Amplify.configure({
  Auth: {
    Cognito: {
      region: import.meta.env.VITE_APP_COGNITO_REGION,
      userPoolId: import.meta.env.VITE_APP_COGNITO_USERPOOLID,
      userPoolClientId: import.meta.env.VITE_APP_COGNITO_USERPOOLWEBCLIENTID,
    },
  },
});

const AuthContext = createContext();

// Wrap your app with <ProvideAuth />
export function ProvideAuth({ children }) {
  const auth = useProvideAuth();
  return <AuthContext.Provider value={auth}>{children}</AuthContext.Provider>;
}

// Access auth values and functions with custom useAuth hook.
// Returns a safe default when ProvideAuth is not mounted (e.g. custom domain routes).
export const useAuth = () => useContext(AuthContext) ?? {};

function useProvideAuth() {
  const { authStatus, user, signOut } = useAuthenticator((context) => [context.user]);
  const queryClient = useQueryClient();
  const hubspot = useMemo(() => (typeof window !== "undefined" && window._hsq ? window._hsq : []), []);

  const { data, error, isLoading } = useQuery({
    queryKey: ["me"],
    queryFn: () => new UserApi().me(),
    enabled: authStatus === "authenticated",
    staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes
    cacheTime: 30 * 60 * 1000, // Keep data in cache for 30 minutes
    retry: 2, // Retry failed requests twice
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000), // Exponential backoff
    onError: (error) => {
      console.error("Failed to fetch user data:", error);
      // Optionally show a toast notification here
    },
  });

  useEffect(() => {
    if (data && hubspot) {
      console.log("Identifying HubSpot user", data.id);
      hubspot.push([
        "identify",
        {
          id: data.id,
          name: data.name,
          email: data.email,
        },
      ]);
      if (window.HubSpotConversations) window.HubSpotConversations.widget.refresh();
    }
  }, [data, hubspot]);

  const userId = data?.id;
  const userName = data?.name;
  const userEmail = data?.email;
  const isAgency = data ? isAgencyUser(data) : false;

  useEffect(() => {
    if (!userId) return;

    // Identify logrocket user
    if (import.meta.env.VITE_APP_LOGROCKET_ID) {
      LogRocket.identify(userId, {
        name: userName,
        email: userEmail,
      });
    }

    // Identify PostHog user with person properties
    const personProps = { name: userName, email: userEmail, roles: data?.roles || [] };
    if (isAgency) {
      personProps.agency_id = data?.organization_id;
    }
    identifyUser(userId, personProps);
    registerSuperProperties({ is_agency_user: isAgency, agency_id: data?.organization_id || null });
  }, [userId, userName, userEmail, isAgency, data?.organization_id]);

  return {
    authStatus,
    user: data,
    isLoading,
    error,
    signOut: () => {
      resetAnalytics();
      signOut();
    },
    hasAccess: (roles) => roles.some((r) => user?.roles && user?.roles.indexOf(r) >= 0),
    refetchUser: () => queryClient.invalidateQueries(["me"]),
  };
}

export async function fetchSession({ forceRefresh } = {}) {
  return fetchAuthSession({ forceRefresh });
}
